import React, { useMemo, useState } from 'react';
import PageHeader from '../../components/shared/PageHeader';
import DataToolbar, { StatusPill, Card } from '../../components/shared/DataToolbar';
import { LoadingState, ErrorState, EmptyResults } from '../../components/shared/Pending';
import { useApi } from '../../api/useApi';
import { listUsers, createUser, updateUser, deleteUser } from '../../api/adminApi';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Plus, MoreHorizontal, Trash2, Edit2, Users2 } from 'lucide-react';
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator } from '../../components/ui/dropdown-menu';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '../../components/ui/alert-dialog';
import { toast } from 'sonner';

const TYPES = ['super_admin', 'admin', 'host'];
const emptyForm = { name: '', email: '', type: 'admin', password: '', password_confirmation: '' };

export default function AdminUsers() {
  const { data, loading, error, refetch } = useApi(listUsers, []);
  const users = useMemo(() => Array.isArray(data) ? data : (data?.items || data || []), [data]);
  const [q, setQ] = useState('');
  const [type, setType] = useState('');
  const [dialog, setDialog] = useState({ open: false, editing: null, form: emptyForm });

  const filtered = users.filter((user) =>
    (!q || `${user.name} ${user.email}`.toLowerCase().includes(q.toLowerCase())) &&
    (!type || user.type === type)
  );

  const openNew = () => setDialog({ open: true, editing: null, form: { ...emptyForm } });
  const openEdit = (user) => setDialog({ open: true, editing: user.id, form: { name: user.name, email: user.email, type: user.type || 'admin', password: '', password_confirmation: '' } });

  const submit = async () => {
    const payload = {
      name: dialog.form.name,
      email: dialog.form.email,
      type: dialog.form.type,
    };
    if (!dialog.editing) {
      payload.password = dialog.form.password;
      payload.password_confirmation = dialog.form.password_confirmation;
    } else if (dialog.form.password) {
      payload.password = dialog.form.password;
      payload.password_confirmation = dialog.form.password_confirmation;
    }

    try {
      if (dialog.editing) {
        await updateUser(dialog.editing, payload);
        toast.success('User updated');
      } else {
        await createUser(payload);
        toast.success('User created');
      }
      setDialog({ open: false, editing: null, form: { ...emptyForm } });
      refetch();
    } catch (e) {
      const first = e?.errors ? Object.values(e.errors).flat()[0] : null;
      toast.error(first || e?.message || 'Save failed');
    }
  };

  const del = async (user) => {
    try {
      await deleteUser(user.id);
      toast.success('User removed');
      refetch();
    } catch (e) {
      toast.error(e?.message || 'Delete failed');
    }
  };

  return (
    <div>
      <PageHeader title="Users" description="Platform staff and host accounts." actions={<Button onClick={openNew} className="bg-teal-700 hover:bg-teal-800 h-10"><Plus className="h-4 w-4 mr-1" /> New user</Button>} />
      <DataToolbar search={q} onSearch={setQ} placeholder="Search users…" filters={[{ label: 'Type', value: type, options: ['All', ...TYPES], onChange: setType }]} />
      {loading ? <LoadingState /> : error ? <ErrorState error={error} onRetry={refetch} /> : filtered.length === 0 ? <EmptyResults icon={Users2} title="No users" description="No users match your filters." /> : (
        <Card className="overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-500 text-xs uppercase tracking-wider"><tr><th className="text-left font-medium px-4 py-3">Name</th><th className="text-left font-medium px-4 py-3">Email</th><th className="text-left font-medium px-4 py-3">Type</th><th className="text-left font-medium px-4 py-3">Subscription / payouts</th><th className="w-10"></th></tr></thead>
            <tbody className="divide-y divide-slate-100">
              {filtered.map((user) => (
                <tr key={user.id} className="hover:bg-slate-50">
                  <td className="px-4 py-3 font-medium text-slate-900">{user.name}</td>
                  <td className="px-4 py-3 text-slate-700">{user.email}</td>
                  <td className="px-4 py-3"><span className="inline-flex items-center px-2 py-0.5 rounded-full bg-slate-100 text-slate-700 text-[11px] font-medium">{(user.type || '').replace('_', ' ')}</span></td>
                  <td className="px-4 py-3">{user.type === 'host' ? <div className="flex gap-2"><StatusPill status={user.subscription?.status || 'pending'} /><StatusPill status={user.stripe_connect?.active ? 'active' : 'pending'} /></div> : <StatusPill status="active" />}</td>
                  <td className="px-4 py-3">
                    <DropdownMenu><DropdownMenuTrigger asChild><button className="h-8 w-8 grid place-items-center rounded-lg hover:bg-slate-100"><MoreHorizontal className="h-4 w-4" /></button></DropdownMenuTrigger><DropdownMenuContent align="end"><DropdownMenuItem onClick={() => openEdit(user)}><Edit2 className="h-4 w-4 mr-2" /> Edit</DropdownMenuItem><DropdownMenuSeparator /><AlertDialog><AlertDialogTrigger asChild><DropdownMenuItem onSelect={(event) => event.preventDefault()} className="text-rose-600"><Trash2 className="h-4 w-4 mr-2" /> Delete</DropdownMenuItem></AlertDialogTrigger><AlertDialogContent><AlertDialogHeader><AlertDialogTitle>Remove user?</AlertDialogTitle><AlertDialogDescription>They will lose access immediately.</AlertDialogDescription></AlertDialogHeader><AlertDialogFooter><AlertDialogCancel>Cancel</AlertDialogCancel><AlertDialogAction className="bg-rose-600 hover:bg-rose-700" onClick={() => del(user)}>Delete</AlertDialogAction></AlertDialogFooter></AlertDialogContent></AlertDialog></DropdownMenuContent></DropdownMenu>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}

      <Dialog open={dialog.open} onOpenChange={(open) => setDialog((current) => ({ ...current, open }))}>
        <DialogContent>
          <DialogHeader><DialogTitle>{dialog.editing ? 'Edit user' : 'New user'}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Full name</Label><Input value={dialog.form.name} onChange={(event) => setDialog((current) => ({ ...current, form: { ...current.form, name: event.target.value } }))} className="mt-1.5" /></div>
            <div><Label>Email</Label><Input value={dialog.form.email} onChange={(event) => setDialog((current) => ({ ...current, form: { ...current.form, email: event.target.value } }))} className="mt-1.5" /></div>
            <div><Label>Account type</Label><Select value={dialog.form.type} onValueChange={(value) => setDialog((current) => ({ ...current, form: { ...current.form, type: value } }))}><SelectTrigger className="mt-1.5"><SelectValue /></SelectTrigger><SelectContent>{TYPES.map((item) => <SelectItem key={item} value={item}>{item.replace('_', ' ')}</SelectItem>)}</SelectContent></Select></div>
            <div><Label>{dialog.editing ? 'New password (optional)' : 'Password'}</Label><Input type="password" value={dialog.form.password} onChange={(event) => setDialog((current) => ({ ...current, form: { ...current.form, password: event.target.value } }))} className="mt-1.5" /></div>
            <div><Label>Confirm password</Label><Input type="password" value={dialog.form.password_confirmation} onChange={(event) => setDialog((current) => ({ ...current, form: { ...current.form, password_confirmation: event.target.value } }))} className="mt-1.5" /></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setDialog((current) => ({ ...current, open: false }))}>Cancel</Button><Button onClick={submit} disabled={!dialog.form.name || !dialog.form.email || (!dialog.editing && (!dialog.form.password || dialog.form.password !== dialog.form.password_confirmation))} className="bg-teal-700 hover:bg-teal-800">{dialog.editing ? 'Save' : 'Create'}</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
